import { apiClient } from './baseApi';

export type DashboardData = {
  user: {
    id: number;
    full_name: string;
    phone: string;
    email: string | null;
    id_number: string;
    status: string;
    is_admin: boolean;
    date_applied: string | null;
    date_joined: string | null;
  };
  chama_config: {
    id: number;
    name: string;
    chama_type: 'merry_go_round' | 'investment' | 'welfare' | 'loan' | 'rotating_business' | 'hybrid';
    contribution_frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'custom';
    contribution_amount: number;
    penalty_per_day: number;
    contribution_start: number;
    contribution_end: number;
    payout_day: number;
    current_cycle: number;
    cycle_state: 'setup' | 'collecting' | 'payout_ready' | 'cycle_active' | 'paused';
    contributions_open: boolean;
    rotation_order: number[];
    is_active: boolean;
    updated_at: string;
  };
  current_contribution: {
    id?: number;
    amount: number;
    month: string;
    status: string;
    payment_method?: string;
    transaction_id?: string | null;
    created_at?: string;
    due_date?: string;
  };
  penalties: {
    unpaid: PenaltyRecord[];
    total_unpaid: number;
  };
  contribution_history: ContributionRecord[];
  payout_history: PayoutRecord[];
};

export type ContributionRecord = {
  id: number;
  amount: number;
  month: string;
  year: number;
  cycle: number;
  status: string;
  payment_method: string | null;
  transaction_id: string | null;
  created_at: string;
};

export type PenaltyRecord = {
  id: number;
  amount: number;
  days_late: number;
  month: string;
  is_paid: boolean;
  created_at: string;
};

export type PayoutRecord = {
  id: number;
  user_id: number;
  user_name: string | null;
  amount: number;
  cycle: number;
  month: string;
  year: number;
  cycle_month?: string; // Keep for backwards compatibility
  status: string;
  payment_method: string | null;
  transaction_id: string | null;
  notes: string | null;
  date_paid: string | null;
  created_at: string;
};

export type NextPayoutInfo = {
  cycle: number;
  position_in_queue: number | null;
  total_pending: number;
  has_received_this_cycle: boolean;
  estimated_months_to_payout: number;
};

export type ProfileData = {
  profile: {
    id: number;
    full_name: string;
    phone: string;
    email: string | null;
    id_number: string;
    status: string;
    is_admin: boolean;
    date_applied: string | null;
    date_joined: string | null;
  };
};

export const MemberApi = {
  async getDashboard(): Promise<DashboardData> {
    const response = await apiClient.get<DashboardData>('/member/dashboard');
    return response.data;
  },

  async getProfile(): Promise<ProfileData> {
    const response = await apiClient.get<ProfileData>('/member/profile');
    return response.data;
  },

  async updateProfile(payload: { email?: string }): Promise<ProfileData> {
    const response = await apiClient.put<ProfileData>('/member/profile', payload);
    return response.data;
  },

  async getContributions(limit = 20, offset = 0): Promise<{
    contributions: ContributionRecord[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await apiClient.get('/member/contributions', {
      params: { limit, offset },
    });
    return response.data;
  },

  async getPenalties(): Promise<{
    unpaid: PenaltyRecord[];
    paid: PenaltyRecord[];
    total_unpaid: number;
    total_paid: number;
  }> {
    const response = await apiClient.get('/member/penalties');
    return response.data;
  },

  async getPayouts(): Promise<{
    payouts: PayoutRecord[];
    total_received: number;
  }> {
    const response = await apiClient.get('/member/payouts');
    return response.data;
  },

  async getNextPayout(): Promise<NextPayoutInfo> {
    const response = await apiClient.get<NextPayoutInfo>('/member/next-payout');
    return response.data;
  },

  async getContributionHistory(): Promise<{
    contributions: {
      id: number;
      user_id: number;
      user_name?: string;
      amount: number;
      month: string;
      year?: number;
      cycle?: number;
      date_paid?: string | null;
      status: string;
      payment_method?: string | null;
      transaction_id?: string | null;
      notes?: string | null;
      created_at: string;
      due_date?: string;
    }[];
    statistics: {
      total_contributed: number;
      paid_count: number;
      unpaid_count: number;
      total_months: number;
    };
  }> {
    const response = await apiClient.get('/member/contribution-history');
    return response.data;
  },
};

export default MemberApi;

